import { useState, useEffect, useCallback, useMemo } from "react";
import { listByUser, InventoryListResponse } from "../../services/adminInventory";

interface UseAdminInventoryParams {
  userId: string;
  page?: number;
  limit?: number;
  q?: string;
  search?: string;
  category?: string;
  condition?: string;
  manufacturer?: string;
  country?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  isActive?: boolean;
}

interface UseAdminInventoryReturn {
  data: InventoryListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAdminInventory = (params: UseAdminInventoryParams): UseAdminInventoryReturn => {
  const [data, setData] = useState<InventoryListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize params to prevent unnecessary re-renders
  const paramsKey = useMemo(
    () => JSON.stringify(params),
    [params],
  );

  const fetchData = useCallback(async () => {
    const currentParams = JSON.parse(paramsKey) as UseAdminInventoryParams;

    if (!currentParams.userId) {
      setData(null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await listByUser(currentParams);
      setData(response.data);
    } catch (err: any) {
      console.error("Error fetching inventory:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch inventory");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
